import { withClient } from './client';
import type { AnalyticsProperties } from './types';

/**
 * Opts the current browser in to tracking. Must run before `identify`/`register`
 * post sign-in — `initAnalytics` starts every session opted out, so calls made
 * before this one are silently ignored.
 */
export const optIn = (): void => {
  withClient((mixpanel) => mixpanel.opt_in_tracking());
};

/**
 * Opts the current browser out of tracking and clears its identity and super
 * properties. Call on sign-out so a shared browser reverts to the same
 * no-analytics state an anonymous visitor gets.
 */
export const optOut = (): void => {
  withClient((mixpanel) => mixpanel.opt_out_tracking());
};

/** Sets the Mixpanel distinct id for the current session. */
export const identify = (distinctId: string): void => {
  withClient((mixpanel) => mixpanel.identify(distinctId));
};

/** Registers super properties, attached to every event from now on. */
export const register = (properties: AnalyticsProperties): void => {
  withClient((mixpanel) => mixpanel.register(properties));
};

/**
 * Records a named event with per-event properties.
 *
 * Silently does nothing when analytics was never started and when the browser
 * is opted out — every session starts opted out, so events fired before
 * `optIn` are dropped, not queued. Callers need no consent guard, but must not
 * fire an event they expect to land before sign-in completes.
 *
 * `app`, `environment` and `page` are stamped by `initAnalytics` on every
 * event; never pass them here.
 */
export const track = (event: string, properties?: AnalyticsProperties): void => {
  withClient((mixpanel) => mixpanel.track(event, properties));
};
